import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { HbsSwitch } from '../components/hbs/Switch';
import { Icon } from '../components/hbs/Icon';
import { EyeToggle } from '../components/hbs/EyeToggle';
import { HbsGenerator } from '../components/hbs/Generator';
import { parseTotp, type ParsedTotp, type CustomField, effectiveWebIdentifiers, effectiveAppIdentifiers, ensureUrlScheme, suggestDomainForName } from '@heybesafe/shared';
import IdentifierListField from '../components/IdentifierListField';
import CustomFieldListField from '../components/CustomFieldListField';

function GenerateAction({ onOpen }: { onOpen: () => void }): JSX.Element {
  return (
    <button
      type="button"
      aria-label="Open password generator"
      id="open-password-generator-button"
      onClick={onOpen}
      className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-accent hover:bg-hbs-surface-alt"
      title="Generate password"
    >
      <Icon name="sparkle" size={16} />
    </button>
  );
}

export default function ItemEditorPage(): JSX.Element {
  // Editor mounts on three routes:
  //   • `/safe/new`                       — create personal item
  //   • `/safe/:id/edit`                  — edit personal item
  //   • `/orgs/:orgId/items/:itemId/edit` — edit shared family item
  // We disambiguate the same way ItemDetailPage does so the lookup can never
  // cross-bleed between personal and shared partitions.
  const params = useParams<{ id?: string; orgId?: string; itemId?: string }>();
  const routeOrgId = params.orgId;
  const id = routeOrgId ? params.itemId : params.id;
  const navigate = useNavigate();
  const { items, addItem, updateItem } = useSafe();

  const existing = id
    ? items.find((i) =>
        routeOrgId ? i.id === id && i.orgId === routeOrgId : i.id === id && !i.orgId,
      )
    : undefined;
  const isEdit = !!existing;
  // No role guard: any member of the owning family may edit a shared item
  // (the item is only in `items` if the viewer is a member who can see it).

  const [name, setName] = useState(existing?.name ?? '');
  const [username, setUsername] = useState(existing?.username ?? '');
  const [password, setPassword] = useState(existing?.password ?? '');
  const [webIdentifiers, setWebIdentifiers] = useState<string[]>(() => {
    if (existing) return effectiveWebIdentifiers(existing);
    // New items start with one empty website row to nudge filling it in (a
    // recommended field). A left-blank row is filtered out on save, so this
    // never persists an empty identifier.
    return [''];
  });
  const [appIdentifiers, setAppIdentifiers] = useState<string[]>(
    () => (existing ? effectiveAppIdentifiers(existing) : []),
  );
  const [group, setGroup] = useState(existing?.group ?? '');
  const [customFields, setCustomFields] = useState<CustomField[]>(existing?.customFields ?? []);
  const [content, setContent] = useState(existing?.content ?? '');
  // orgId is read-only here — the org context comes from the route, not a dropdown.
  const [orgId] = useState(existing?.orgId ?? '');
  const [sensitive, setSensitive] = useState(existing?.sensitive ?? false);
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [totp, setTotp] = useState(existing?.totp ?? '');
  const [totpRevealed, setTotpRevealed] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [totpHint, setTotpHint] = useState<string | null>(null);

  // New-login only: when the user finishes typing the name and hasn't entered
  // any website yet, pre-fill the primary website from the on-device brand →
  // domain lookup. Never overwrites a website the user already typed.
  function fillWebsiteFromName(): void {
    if (isEdit) return;
    if (webIdentifiers.some((u) => u.trim())) return;
    const domain = suggestDomainForName(name);
    if (!domain) return;
    setWebIdentifiers((prev) => {
      const next = [...prev];
      next[0] = domain;
      return next;
    });
  }

  function validateTotp(value: string): void {
    if (!value) {
      setTotpError(null);
      setTotpHint(null);
      return;
    }
    try {
      const p: ParsedTotp = parseTotp(value);
      setTotpError(null);
      const isDefault = p.period === 30 && p.digits === 6 && p.algorithm === 'SHA1';
      setTotpHint(
        isDefault
          ? null
          : `Detected: ${p.period}s period, ${p.digits} digits, ${p.algorithm}`,
      );
    } catch (e) {
      setTotpError(e instanceof Error ? e.message : 'Invalid TOTP secret');
      setTotpHint(null);
    }
  }

  const existingGroups = Array.from(
    new Set(items.map((i) => i.group).filter((g): g is string => !!g))
  ).sort();

  const isPasskey = existing?.type === 'passkey';

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim()) return;
    if (totpError) return;
    setLoading(true);
    try {
      if (isPasskey && existing) {
        // Passkey editing is name-only — every other field is immutable
        // (managed by the WebAuthn ceremony). Emit just the new name plus
        // the standard metadata; do NOT include type/identifiers/url/
        // username/password/etc. so the merge can't clobber the passkey-
        // specific fields (rpId, credentialId, keys). Mirrors the popup
        // ItemEditor T19 partial-save behavior.
        await updateItem(existing.id, {
          name: name.trim(),
          favorite: existing.favorite ?? false,
          tags: existing.tags ?? [],
        });
        navigate(
          existing.orgId
            ? `/orgs/${existing.orgId}/items/${existing.id}`
            : `/safe/${existing.id}`,
        );
        return;
      }
      const payload = {
        // Preserve the item's existing type on edit (a note, card, etc. must
        // not be silently converted to a login by passing through the generic
        // editor); only a brand-new generic item defaults to 'login'. Cards
        // route to the dedicated CardEditorPage, but keep this correct so the
        // generic editor can never mangle one that reaches it.
        type: existing?.type ?? ('login' as const),
        name: name.trim(),
        username: username.trim() || undefined,
        password: password || undefined,
        // Recombined web-first, android-app-second. Mixed-type ordering from
        // the original item isn't preserved — the two-section editor has no
        // way to represent it. Acceptable because order has no semantic meaning.
        // ensureUrlScheme: IdentifierListField only normalizes on blur, so a
        // user who types `example.com` and clicks Save without blurring first
        // commits a bare hostname. Normalize at the save site so storage is
        // always a parseable URL — downstream launch + match paths assume it.
        identifiers: [
          ...webIdentifiers.filter((v) => v.trim()).map((v) => ({ type: 'web' as const, value: ensureUrlScheme(v.trim()) })),
          ...appIdentifiers.filter((v) => v.trim()).map((v) => ({ type: 'android-app' as const, value: v.trim() })),
        ],
        url: undefined,
        group: group.trim() || undefined,
        // Drop blank rows (no name); keep the value verbatim so secrets with
        // intentional whitespace round-trip unchanged.
        customFields: (() => {
          const cleaned = customFields
            .filter((f) => f.name.trim())
            .map((f) => ({ name: f.name.trim(), value: f.value, ...(f.hidden ? { hidden: true } : {}) }));
          return cleaned.length ? cleaned : undefined;
        })(),
        content: content.trim() || undefined,
        totp: totp.trim() || undefined,
        sensitive,
        orgId: orgId || undefined,
        favorite: existing?.favorite ?? false,
        tags: existing?.tags ?? [],
      };
      if (isEdit && existing) {
        await updateItem(existing.id, payload);
        // Return to the same detail route the editor was launched from so the
        // back-stack and badges stay consistent for shared items.
        navigate(
          existing.orgId
            ? `/orgs/${existing.orgId}/items/${existing.id}`
            : `/safe/${existing.id}`,
        );
      } else {
        await addItem(payload);
        navigate('/safe');
      }
    } finally {
      setLoading(false);
    }
  }

  if (showGenerator) {
    return (
      <div className="p-7 max-w-2xl">
        <h2 className="font-display text-[20px] text-hbs-text mb-4">Password Generator</h2>
        <HbsGenerator
          onSelect={(pw) => {
            setPassword(pw);
            setShowGenerator(false);
          }}
          onClose={() => setShowGenerator(false)}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col max-w-3xl">
      <form id="item-editor-form" onSubmit={handleSubmit} className="flex-1 min-h-0 overflow-y-auto p-7 flex flex-col gap-4">
        <h2 className="font-display font-semibold text-[24px] tracking-hbs-tight text-hbs-text mb-2">
          {isEdit && existing ? `Edit ${existing.name}` : 'New Login'}
        </h2>
        <HbsInput label="Name" value={name} onChange={setName} onBlur={fillWebsiteFromName} placeholder="e.g. GitHub" required autoFocus name="name" recommended={!isEdit} />
        {isPasskey && (
          <p className="text-[12px] text-hbs-text-mute">
            Passkey details (site, username, credential) are read-only and can't be changed here.
          </p>
        )}
        {!isPasskey && (
          <>
            <HbsInput
              label="Username / Email"
              value={username}
              onChange={setUsername}
              placeholder="you@example.com"
              name="username"
              recommended={!isEdit}
            />
            <HbsInput
              label="Password"
              type={revealed ? 'text' : 'password'}
              value={password}
              onChange={setPassword}
              mono={revealed}
              name="password"
              recommended={!isEdit}
              trailingActions={
                <>
                  <GenerateAction onOpen={() => setShowGenerator(true)} />
                  <EyeToggle revealed={revealed} onChange={setRevealed} />
                </>
              }
            />
            <div>
              <HbsInput
                label="Two-factor code"
                type={totpRevealed ? 'text' : 'password'}
                value={totp}
                onChange={(value) => {
                  setTotp(value);
                  setTotpError(null);
                  setTotpHint(null);
                }}
                onBlur={() => validateTotp(totp)}
                placeholder="Base32 secret or otpauth:// URI"
                name="totp"
                mono={totpRevealed}
                trailingActions={<EyeToggle revealed={totpRevealed} onChange={setTotpRevealed} />}
              />
              {totpError && (
                <p className="text-[12px] text-hbs-warning mt-1">{totpError}</p>
              )}
              {totpHint && (
                <p className="text-[12px] text-hbs-text-mute mt-1">{totpHint}</p>
              )}
            </div>
            <IdentifierListField kind="web" values={webIdentifiers} onChange={setWebIdentifiers} recommended={!isEdit} />
            <IdentifierListField kind="android-app" values={appIdentifiers} onChange={setAppIdentifiers} />

            <CustomFieldListField values={customFields} onChange={setCustomFields} />

            <div>
              <label
                htmlFor="group-input"
                className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1"
              >
                Group
              </label>
              <input
                id="group-input"
                aria-label="Group"
                type="text"
                list="group-options"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                placeholder="e.g. Work, Personal"
                className="w-full h-12 px-3 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20"
              />
              <datalist id="group-options">
                {existingGroups.map((g) => (
                  <option key={g} value={g} />
                ))}
              </datalist>
            </div>

            <div>
              <label
                htmlFor="notes-input"
                className="block text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1"
              >
                Notes
              </label>
              <textarea
                id="notes-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Any additional notes…"
                rows={3}
                className="w-full px-3 py-2 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20 resize-none"
              />
            </div>

            <div className="flex items-start gap-3">
              <HbsSwitch
                id="sensitive-toggle"
                checked={sensitive}
                onChange={setSensitive}
              />
              <div>
                <label
                  htmlFor="sensitive-toggle"
                  className="block text-[14px] text-hbs-text cursor-pointer"
                >
                  Sensitive
                </label>
                <p className="text-[12px] text-hbs-text-mute mt-0.5">
                  Keeps the username hidden in the safe list and pushes the entry to the bottom.
                </p>
              </div>
            </div>
          </>
        )}
      </form>
      <div className="shrink-0 border-t border-hbs-border bg-hbs-bg px-7 py-4 flex gap-2">
        <HbsButton id="item-save-button" type="submit" form="item-editor-form" loading={loading} fullWidth>
          Save
        </HbsButton>
        <HbsButton
          id="item-cancel-button"
          type="button"
          variant="ghost"
          fullWidth
          onClick={() =>
            navigate(
              isEdit && existing
                ? existing.orgId
                  ? `/orgs/${existing.orgId}/items/${existing.id}`
                  : `/safe/${existing.id}`
                : '/safe',
            )
          }
        >
          Cancel
        </HbsButton>
      </div>
    </div>
  );
}
